// @ts-nocheck
/* Starter Custom File */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import { CarouselArrowPresentationProps } from "@store/Library/Components/Toolkit/Carousel/CarouselArrow";
import { CarouselButtonPresentationProps } from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import Carousel, {
    carouselDefaultCustomCss,
    CarouselPresentationProps,
    CarouselSlide,
} from "@store/Overrides/Components/Carousel";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import mergeBreakpointValues from "@toolkit/Framework/Common/Utilities/mergeBreakpointValues";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import * as cssLinter from "css";
// import React, { useMemo } from "react"; // Foundation
// import { css } from "styled-components"; // Foundation

// Foundation --->
import ArrowBackward from "@store/Library/Components/Icons/ArrowBackward";
import { carouselButtonStyle } from "@store/Themes/common/props/button";
import { DotButton } from "@store/Themes/common/props/dotButton";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { css, FlattenInterpolation, ThemeProps, useTheme } from "styled-components";
// <--- Foundation

const buttonSize = pxToRemArray([32, null, null, null, 43]);
const controlsSpacing = pxToRemArray([15]);
const dotsSize = pxToRemArray([8, null, 16]);
const inlineArrowsBreakpoint: BreakpointKey = "sm";

const buttonProtectionSpacing = mergeBreakpointValues(buttonSize, controlsSpacing, (x, y) => `calc(${x} + ${y})`);
const dotProtectionSpacing = mergeBreakpointValues(dotsSize, controlsSpacing, (x, y) => `calc(${x} + ${y})`);

const carouselButtonProps: CarouselButtonPresentationProps = {
    size: buttonSize,
};

const carouselArrowProps: CarouselArrowPresentationProps = {
    extendedStyles: {
        carouselButton: carouselButtonProps,
    },
    positionOffset: pxToRemArray([5, null, null, null, 10]),
    positionOrigin: 0,
};

/* Store Custom Code - Start */
interface SlideModel {
    fields: {
        slideTitle: string;
        slideLink: LinkFieldValue; // Foundation
        background: "image" | "color";
        image: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        customCSS: string;
    };
}
/* Store Custom Code - End */

const enum fields {
    autoplay = "autoplay",
    autoplayDelay = "autoplayDelay",
    customCSS = "customCSS",
    items = "items",
    showArrows = "showArrows",
    showDots = "showDots",
    // Foundation
    height = "height",
    textAlignment = "textAlignment",
    slideIndicator = "slideIndicator",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    includeBorder = "includeBorder",
    backgroundColor = "backgroundColor",
    maximumSlide = "maximumSlide",
    title = "title",
    coloredHeader = "coloredHeader",
    altArrow = "altArrow",
    isUseLegacySlideshow = "isUseLegacySlideshow",
}

const enum itemFields {
    id = "id",
    name = "name",
}

interface OwnProps extends WidgetProps, ExpandStyles<SlideshowStyles> {
    fields: {
        [fields.autoplay]: boolean;
        [fields.autoplayDelay]: number;
        [fields.customCSS]: string;
        [fields.items]: [
            {
                fields: {
                    [itemFields.id]: string;
                    [itemFields.name]: string;
                };
            },
        ];
        [fields.showArrows]: boolean;
        [fields.showDots]: boolean;
        // Foundation
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        [fields.title]: string;
        [fields.maximumSlide]: number;
        [fields.coloredHeader]: boolean;
        [fields.backgroundColor]: string;
        [fields.includeBorder]: boolean;
        [fields.altArrow]: boolean;
        [fields.isUseLegacySlideshow]: boolean;
    };
}

export interface SlideshowStyles {
    carousel?: CarouselPresentationProps;
    // Foundation
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    titleText?: TypographyPresentationProps;
    mainWrapper?: InjectableCss;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    nextArrowGridItem?: GridItemProps;
    carouselGridItem?: GridItemProps;
}

export const slideshowStyles: SlideshowStyles = {
    carousel: {
        extendedStyles: {
            carouselArrowPrevious: carouselArrowProps,
            carouselArrowNext: carouselArrowProps,
            carouselPause: carouselButtonProps,
            carouselPlay: carouselButtonProps,
            carouselControls: {
                css: css`
                    position: absolute;
                    ${breakpointValues(controlsSpacing, "bottom")}
                    margin: 0;
                `,
            },
            carouselDots: {
                variant: "outline",
                size: dotsSize,
            },
            carouselSlide: {
                css: css`
                    > div /* Zone */ {
                        height: 100%;

                        > [data-widget="Basic/Banner"] /* Preview Mode */ {
                            height: 100%;
                        }
                    }

                    .banner {
                        min-height: 100%;
                    }

                    .carousel.has-dot-controls && {
                        .banner-inner-wrapper {
                            ${breakpointValues(dotProtectionSpacing, "margin-bottom")}
                        }

                        > [class^="ZoneStyle"] > [class^="ZoneWrapper"] > [class^="AddContainer"] /* Edit Mode */ {
                            ${breakpointValues(dotProtectionSpacing, "padding-bottom")}
                        }
                    }

                    .carousel.has-button-controls && {
                        .banner-inner-wrapper {
                            ${breakpointValues(buttonProtectionSpacing, "margin-bottom")}
                        }

                        > [class^="ZoneStyle"] > [class^="ZoneWrapper"] > [class^="AddContainer"] /* Edit Mode */ {
                            ${breakpointValues(buttonProtectionSpacing, "padding-bottom")}
                        }
                    }

                    > [class^="ZoneStyle"] > [class^="ZoneWrapper"] /* Edit Mode */ {
                        height: 100%;

                        > [data-widget="Basic/Banner"] {
                            height: 100%;

                            > [class^="WidgetStyle"] {
                                height: 100%;

                                > [class^="WidgetWrapper"] {
                                    height: 100%;
                                }
                            }
                        }

                        > [class^="AddContainer"]:not(:first-child) {
                            display: none;
                        }
                    }
                `,
            },
        },
        inlineArrowsBreakpoint,
        itemsGap: 0,
    },
    /* Foundation Custom Code - Start */
    slideshowWrapper: {
        css: css`
            padding: ${pxToRem(10)} 0;
            position: relative;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: ${pxToRem(-20)};
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;

            &:hover {
                background: none;
            }

            ${ButtonIcon} {
                color: ${getColor("primary")};
            }
        `,
    },
    iconProps: {
        size: 35,
        color: "primary",
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            position: relative;
            width: 100%;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            position: absolute;
            bottom: 1em;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: ${pxToRem(3)};
            width: ${pxToRem(30)};
            padding: 0;
            margin: 0 ${pxToRem(4)};
            border: 0;
            background: ${getColor("common.border")};

            &:hover,
            &:active {
                background: ${getColor("common.border")};
            }

            &:focus {
                outline: ${pxToRem(4)} solid ${getColor("common.outline.color")};
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: ${pxToRem(-20)};
            right: 0;
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }

            ${ButtonIcon} {
                color: ${getColor("primary")};
            }
        `,
    },
    // Foundation
    mainWrapper: {
        css: css`
            position: relative;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            text-align: center;
            margin: 0;
            padding: ${pxToRem(10)} 0;
            background-color: ${getColor("primary.main")};
            font-weight: ${({ theme }) => theme.design.typography.weight.base};
        `,
    },
    mainContainer: {
        gap: 0,
        offsetCss: css`
            justify-content: center;
            margin: 0 auto;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        width: 85%;
                    `,
                    css``,
                    css`
                        width: 93%;
                    `,
                ])};
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: relative;
            justify-content: flex-end;
            z-index: 100;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: relative;
                        flex-basis: 5%;
                        max-width: 5%;
                        left: 2em;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: relative;
            justify-content: flex-start;
            z-index: 100;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        position: relative;
                        flex-basis: 5%;
                        max-width: 5%;
                        right: 2em;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])};
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])};
        `,
    },
    /* Foundation Custom Code - End */
};

/* Store Custom Code - Start */
const defaultCustomCss = `
&${carouselDefaultCustomCss}

.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;
/* Store Custom Code - End */

export const Slideshow = ({
    extendedStyles,
    fields: {
        autoplay,
        autoplayDelay,
        customCSS,
        items,
        showArrows,
        showDots,
        // Foundation
        height,
        textAlignment,
        slideIndicator,
        responsiveFontSizes,
        customFontSizes,
        h1FontSize,
        h2FontSize,
        h3FontSize,
        h4FontSize,
        h5FontSize,
        normalFontSize,
        title,
        maximumSlide,
        coloredHeader,
        backgroundColor,
        includeBorder,
        altArrow,
        isUseLegacySlideshow,
        slides: legacySlides,
    },
    fields: allFields,
    id,
    overridedStyles,
}: OwnProps) => {
    const currentPage = useCurrentPage();
    const { isEditing, pageId: shellPageId } = useShellContext();
    const styles = useExpandProps(slideshowStyles, extendedStyles, overridedStyles);

    const zoneNames: string[] = [];
    const slides: CarouselSlide[] = items.map((item, index) => {
        const zoneName = `Item-${item.fields.id || index}`;
        zoneNames.push(zoneName);

        return {
            id: `item-${id}-${item.fields.id || index}`,
            renderComponent: () => <Zone contentId={id} zoneName={zoneName} />,
        };
    });

    const zonesWidgets = useAppSelector(state => {
        const pageId = shellPageId || currentPage.id;
        const zones = state.data.pages.widgetIdsByPageIdParentIdAndZone[pageId]?.[id];

        return zoneNames.map(x => zones?.[x]?.map(y => state.data.pages.widgetsById[y]));
    });

    const carouselStyles = useMemo(() => {
        return mergeProps<SlideshowStyles["carousel"]>(styles.carousel, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${customCSS}
                    `,
                },
            },
        });
    }, [customCSS]);

    if (!isEditing) {
        /* Store Custom Code - Start */
        if ((!isUseLegacySlideshow && !slides.length) || (isUseLegacySlideshow && !legacySlides.length)) {
            return null;
        }

        if (!isUseLegacySlideshow && slides.length === 1) {
            return <>{slides[0]}</>;
        }

        if (isUseLegacySlideshow && legacySlides.length === 1) {
            return <>{legacySlides[0]}</>;
        }
        /* Store Custom Code - End */
    }

    const invertColorIndexes = zonesWidgets.reduce<number[]>((result, widget, index) => {
        return widget?.find(x => x.type === "Basic/Banner")?.fields.invertColor ? [...result, index] : result;
    }, []);

    /* Foundation Custom Code - Start */
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(legacySlides, o => o.fields.buttonLink);
    const slideLinks = useGetLinks(legacySlides, o => o.fields.slideLink); // Foundation
    const theme = useTheme(); // Foundation

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, autoplayDelay * 1000);

    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [slideWidth, setSlideWidth] = useState("100%");

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
        const width = (100 / newSlidesToScroll).toString();
        setSlideWidth(`${width}%`);
        if (embla) {
            setScrollSnaps(embla.scrollSnapList());
        }
    }, [maximumSlide, windowResizeTime]);

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maximumSlide;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maximumSlide);
    }, [maximumSlide]);

    const [slidesToScroll, setSlidesToScroll] = useState(getSlidesToScroll());

    useEffect(() => {
        if (!embla) {
            return;
        }

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
            setScrollSnaps(embla.scrollSnapList());
        };

        const maxScrollSnaps = Math.ceil(fields.slides.length / slidesToScroll);
        if (scrollSnaps.length > maxScrollSnaps) {
            scrollSnaps.splice(maxScrollSnaps, scrollSnaps.length);
        }

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla, slidesToScroll]);

    const onSelect = useCallback(() => {
        if (!embla) {
            return;
        }
        setSelectedIndex(embla.selectedScrollSnap());
    }, [embla, setSelectedIndex]);

    const dotClickHandler = (index: number) => {
        setSelectedIndex(index);
        embla?.scrollTo(index);
    };

    useEffect(() => {
        if (!embla) {
            return;
        }
        onSelect();
        setScrollSnaps(embla.scrollSnapList());

        embla.on("select", onSelect);
    }, [embla, onSelect]);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        setScrollSnaps(embla.scrollSnapList());

        embla.reInit({
            align: "start",
            loop: true,
            slidesToScroll,
            draggable: true,
        });
    }, [embla, fields.slides, slidesToScroll]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    /* Store Custom Code - Start */
    if (
        (!isUseLegacySlideshow && (!slides || slides.length === 0)) ||
        (isUseLegacySlideshow && (!legacySlides || legacySlides.length === 0))
    ) {
        return null;
    }
    /* Store Custom Code - End */

    let heightStyles: string;
    switch (height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const mainWrapperbackgroundColor = `background-color: ${fields.backgroundColor};`;
    const mainWrapperBorder = (): FlattenInterpolation<ThemeProps<BaseTheme>> => {
        return includeBorder
            ? css`
                  border-bottom: ${pxToRem(10)} solid ${getColor("common.borderColored")};
              `
            : css`
                  border-bottom: none;
              `;
    };

    const containerStyle: GridContainerProps = {
        ...styles.mainContainer,
        offsetCss: css`
            ${styles.mainContainer?.css};
            ${mainWrapperbackgroundColor};
            ${mainWrapperBorder()};
        `,
    };

    const titleStyle: TypographyPresentationProps = {
        ...styles.titleText,
        color: `${coloredHeader ? "primary.contrast" : "primary.main"}`,
        css: css`
            ${styles.titleText?.css ?? ""}
            ${!coloredHeader ? "background: none" : ""}
        `,
    };

    const arrowForwardStyle = altArrow
        ? {
              ...styles.nextArrowButton,
              ...carouselButtonStyle("right"),
          }
        : { ...styles.nextArrowButton };
    const arrowBackwardStyle = altArrow
        ? {
              ...styles.prevArrowButton,
              ...carouselButtonStyle("left"),
          }
        : { ...styles.prevArrowButton };
    /* Foundation Custom Code - End */

    /* Store Custom Code - Start - add isUseLegacySlideshow */
    return isUseLegacySlideshow ? (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.mainWrapper}>
                <Typography {...titleStyle}>{title /* Store Custom Code */}</Typography>
                <GridContainer {...containerStyle}>
                    {showArrows && fields.slides.length > 1 && (
                        <GridItem {...styles.prevArrowGridItem}>
                            <Button
                                {...arrowBackwardStyle}
                                onClick={() => {
                                    embla && embla.scrollPrev();
                                    onSelect();
                                }}
                            >
                                <ButtonIcon {...styles.iconProps} src={altArrow ? ArrowBackward : ChevronLeft} />
                                <VisuallyHidden>{translate("Next slide")}</VisuallyHidden>
                            </Button>
                        </GridItem>
                    )}
                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                                {legacySlides.map((slide, index) => {
                                    const buttonLink = buttonLinks[index];
                                    const slideLink = slideLinks[index];

                                    let responsiveImageBehaviorStyles;
                                    if (slide.fields.responsiveImageBehavior === "cover") {
                                        responsiveImageBehaviorStyles = "background-size: cover;";
                                    } else if (slide.fields.responsiveImageBehavior === "center") {
                                        responsiveImageBehaviorStyles = "background-size: contain;";
                                    } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                        responsiveImageBehaviorStyles = "background-size: auto 100%;";
                                    } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                        responsiveImageBehaviorStyles = "background-size: 100% auto;";
                                    }

                                    const backgroundStyles =
                                        slide.fields.background === "image"
                                            ? `background-image: url(${slide.fields.image});`
                                            : `background-color: ${slide.fields.backgroundColor};`;
                                    const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                                    let overlayPositioningStyles;
                                    if (!slide.fields.partialOverlay) {
                                        overlayPositioningStyles = "align-items: stretch;";
                                    } else if (slide.fields.partialOverlayPositioning === "top") {
                                        overlayPositioningStyles = "align-items: flex-start;";
                                    } else if (slide.fields.partialOverlayPositioning === "middle") {
                                        overlayPositioningStyles = "align-items: center;";
                                    } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                        overlayPositioningStyles = "align-items: flex-end;";
                                    }

                                    let fontSizeStyles;
                                    if (responsiveFontSizes || customFontSizes) {
                                        fontSizeStyles = responsiveStyleRules(
                                            responsiveFontSizes,
                                            customFontSizes ? allFields : undefined,
                                        );
                                    }

                                    const slideWrapperStyles = {
                                        css: css`
                                            ${styles.slideContentWrapper?.css || ""}
                                            ${heightStyles}
                                    ${textAlignStyles}
                                    ${backgroundStyles}
                                    ${responsiveImageBehaviorStyles}
                                    ${focalPointStyles}
                                    ${overlayPositioningStyles}
                                    ${fontSizeStyles}
                                        max-width: ${slideWidth};
                                        `,
                                    };
                                    const slideOverlayWrapperStyles = {
                                        css: css`
                                            ${styles.slideOverlayWrapper?.css || ""}
                                            background-color: ${slide.fields.background === "image"
                                                ? slide.fields.imageOverlay
                                                : ""};
                                            padding: ${slide.fields.contentPadding}px;
                                            ${slide.fields.centerTextVertically &&
                                            `
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;`}
                                        `,
                                    };
                                    return (
                                        <StyledWrapper
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            className="slide-wrapper"
                                            onClick={() => onClick(slideLink?.url)}
                                            {...slideWrapperStyles}
                                        >
                                            <StyledWrapper {...slideOverlayWrapperStyles}>
                                                <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                                    {slide.fields.heading && (
                                                        <Typography className="slide-heading" {...styles.headingText}>
                                                            {parse(slide.fields.heading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                    {slide.fields.subheading && (
                                                        <Typography
                                                            className="slide-subheading"
                                                            {...styles.subheadingText}
                                                        >
                                                            {parse(slide.fields.subheading, parserOptions)}
                                                        </Typography>
                                                    )}
                                                    {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                        <Button
                                                            className="slide-button"
                                                            {...styles.slideButton}
                                                            variant={slide.fields.buttonVariant}
                                                            onClick={() => onClick(buttonLink?.url)}
                                                        >
                                                            {slide.fields.buttonLabel ||
                                                                buttonLink?.title ||
                                                                buttonLink?.url}
                                                        </Button>
                                                    )}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                    {slideIndicator && (
                        <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                            {scrollSnaps.map((scrollSnap, index) => {
                                return (
                                    <DotButton
                                        className="slide-dot-button"
                                        key={scrollSnap}
                                        selected={index === selectedIndex}
                                        altColor
                                        onClick={() => dotClickHandler(index)}
                                        aria-label={
                                            index === selectedIndex
                                                ? translate("Current Slide")
                                                : translate(`Go to slide ${index}`)
                                        }
                                    />
                                );
                            })}
                        </StyledWrapper>
                    )}
                    {showArrows && fields.slides.length > 1 && (
                        <GridItem className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                            <Button
                                className="slide-arrow"
                                {...arrowForwardStyle}
                                onClick={() => {
                                    embla && embla.scrollNext();
                                    onSelect();
                                }}
                            >
                                <ButtonIcon {...styles.iconProps} src={altArrow ? ArrowForward : ChevronRight} />
                                <VisuallyHidden>{translate("Previous slide")}</VisuallyHidden>
                            </Button>
                        </GridItem>
                    )}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    ) : (
        <Carousel
            {...carouselStyles}
            autoplay={autoplay}
            autoplayDelay={autoplayDelay * 1000}
            id={`slideshow-${id}`}
            invertColor={invertColorIndexes}
            itemsGap={0}
            showDots={showDots}
            showArrows={showArrows}
            slides={slides}
        />
    );
    /* Store Custom Code - End - add isUseLegacySlideshow */
};

// const defaultCustomCss = `&${carouselDefaultCustomCss}`; // Foundation

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const advancedTab = { displayName: "Advanced", sortOrder: 1 };
const legacyTab = { displayName: "Legacy", sortOrder: 2 }; // Foundation

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            /* Foundation Custom Code - Start */
            {
                name: fields.isUseLegacySlideshow,
                displayName: "Use Legacy Banner",
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                variant: "toggle",
                defaultValue: true,
            },
            /* Foundation Custom Code - End */
            {
                name: fields.showArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
                isVisible: x => !x.fields.isUseLegacySlideshow, // Store Custom Code
            },
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
                isVisible: x => !x.fields.isUseLegacySlideshow, // Store Custom Code
            },
            {
                name: fields.autoplay,
                displayName: "Enable autoplay",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: x => !x.fields.isUseLegacySlideshow, // Store Custom Code
            },
            {
                name: fields.autoplayDelay,
                displayName: "Autoplay delay",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 8,
                min: 1,
                isVisible: x => x.fields.autoplay && !x.fields.isUseLegacySlideshow, // Store Custom Code
                tooltip: `The number of seconds to wait before changing slide, autoplay will remain disabled while editing`,
                tooltipPosition: "left",
            },
            {
                name: fields.items,
                getDisplay: x => x.fields.name || translate("Item"),
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: itemFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: itemFields.name,
                        displayName: "Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                isVisible: x => !x.fields.isUseLegacySlideshow, // Store Custom Code
            },
            /* Foundation Custom Code - Start */
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: false,
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.maximumSlide,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "4", value: 4 },
                    { displayName: "3", value: 3 },
                    { displayName: "2", value: 2 },
                    { displayName: "1", value: 1 },
                ],
                defaultValue: 4,
                hideEmptyOption: true,
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.coloredHeader,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.backgroundColor,
                fieldType: "General",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.includeBorder,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: legacyTab,
                defaultValue: true,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.altArrow,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: x => x.fields.showArrows && !x.fields.isUseLegacySlideshow,
                tab: legacyTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: legacyTab,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: legacyTab,
                defaultValue: true,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: legacyTab,
                defaultValue: false,
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: legacyTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "slideLink",
                        displayName: "Slide Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        isVisible: widget => widget.fields.partialOverlay,
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "center", // Foundation
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                isVisible: x => x.fields.isUseLegacySlideshow,
            },
            /* Foundation Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
